import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import Chart from "react-apexcharts";
import {
  IconChartBar,
  IconUsers,
  IconUserPlus,
  IconTrendingUp,
  IconPhone,
  IconMail,
  IconMapPin,
  IconTag,
  IconCalendar,
} from "@tabler/icons-react";
import { getCustomerStats } from "../services/CustomerServices";

const CustomerStatsModal = ({ show, onHide }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) {
      fetchStats();
    }
  }, [show]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await getCustomerStats();
      setStats(response.data);
    } catch (error) {
      console.error("Erro ao buscar estatísticas:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerTypeChartData = () => {
    if (!stats?.customers_by_type) return null;

    const labels = [];
    const series = [];
    const colors = ["#28a745", "#007bff", "#ffc107", "#6f42c1"];

    Object.entries(stats.customers_by_type).forEach(([type, count]) => {
      const typeLabels = {
        new: "Novos",
        regular: "Regulares",
        vip: "VIP",
        returning: "Retornando",
      };
      labels.push(typeLabels[type] || type);
      series.push(count);
    });

    return {
      options: {
        chart: {
          type: "donut",
          height: 300,
        },
        labels: labels,
        colors: colors,
        legend: {
          position: "bottom",
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            return Math.round(val) + "%";
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " clientes";
            },
          },
        },
      },
      series: series,
    };
  };

  const getAcquisitionChannelChartData = () => {
    if (!stats?.customers_by_acquisition_channel) return null;

    const labels = [];
    const series = [];

    Object.entries(stats.customers_by_acquisition_channel).forEach(
      ([channel, count]) => {
        const channelLabels = {
          website: "Site",
          whatsapp: "WhatsApp",
          instagram: "Instagram",
          facebook: "Facebook",
          google: "Google",
          referral: "Indicação",
          walk_in: "Entrada Direta",
          advertising: "Publicidade",
          other: "Outros",
        };
        labels.push(channelLabels[channel] || channel);
        series.push(count);
      }
    );

    return {
      options: {
        chart: {
          type: "bar",
          height: 300,
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
          },
        },
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: labels,
        },
        colors: ["#007bff"],
        tooltip: {
          y: {
            formatter: function (val) {
              return val + " clientes";
            },
          },
        },
      },
      series: [
        {
          name: "Clientes",
          data: series,
        },
      ],
    };
  };

  const StatCard = ({ icon, title, value, subtitle, color = "primary" }) => (
    <Card className="text-center h-100">
      <Card.Body>
        <div className={`text-${color} mb-2`}>{icon}</div>
        <h4 className="mb-1">{value}</h4>
        <h6 className="text-muted mb-0">{title}</h6>
        {subtitle && <small className="text-muted">{subtitle}</small>}
      </Card.Body>
    </Card>
  );

  const TopTagsList = ({ tags }) => {
    if (!tags || tags.length === 0) {
      return <p className="text-muted text-center">Nenhuma tag encontrada</p>;
    }

    return (
      <div>
        {tags.map((tag, index) => (
          <div
            key={index}
            className="d-flex justify-content-between align-items-center mb-2"
          >
            <div className="d-flex align-items-center">
              <div
                className="rounded-circle me-2"
                style={{
                  width: "12px",
                  height: "12px",
                  backgroundColor: tag.tag__color || "#6c757d",
                }}
              ></div>
              <span>{tag.tag__name}</span>
            </div>
            <Badge bg="secondary">{tag.count}</Badge>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconChartBar className="me-2" />
          Estatísticas de Clientes
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center p-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Carregando estatísticas...</p>
          </div>
        ) : stats ? (
          <>
            {/* Cards de Resumo */}
            <Row className="mb-4">
              <Col md={3} className="mb-3">
                <StatCard
                  icon={<IconUsers size={32} />}
                  title="Total de Clientes"
                  value={stats.total_customers}
                  color="primary"
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatCard
                  icon={<IconUserPlus size={32} />}
                  title="Clientes Ativos"
                  value={stats.active_customers}
                  subtitle={`${Math.round(
                    (stats.active_customers / stats.total_customers) * 100
                  )}% do total`}
                  color="success"
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatCard
                  icon={<IconTrendingUp size={32} />}
                  title="Novos Este Mês"
                  value={stats.new_customers_this_month}
                  color="info"
                />
              </Col>
              <Col md={3} className="mb-3">
                <StatCard
                  icon={<IconPhone size={32} />}
                  title="WhatsApp Ativo"
                  value={`${stats.whatsapp_active_percentage}%`}
                  subtitle={`${Math.round(
                    (stats.whatsapp_active_percentage / 100) *
                      stats.total_customers
                  )} clientes`}
                  color="success"
                />
              </Col>
            </Row>

            {/* Informações Adicionais */}
            <Row className="mb-4">
              <Col md={4}>
                <Card>
                  <Card.Body className="text-center">
                    <IconCalendar size={24} className="text-info mb-2" />
                    <h6>Idade Média</h6>
                    <h4 className="text-info">
                      {stats.avg_age ? `${stats.avg_age} anos` : "N/A"}
                    </h4>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body className="text-center">
                    <IconTag size={24} className="text-warning mb-2" />
                    <h6>Tags Mais Usadas</h6>
                    <h4 className="text-warning">
                      {stats.top_tags?.length || 0}
                    </h4>
                    <small className="text-muted">tags diferentes</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card>
                  <Card.Body className="text-center">
                    <IconMail size={24} className="text-secondary mb-2" />
                    <h6>Com E-mail</h6>
                    <h4 className="text-secondary">
                      {Math.round(
                        ((stats.total_customers -
                          (stats.customers_by_type?.no_email || 0)) /
                          stats.total_customers) *
                          100
                      )}
                      %
                    </h4>
                    <small className="text-muted">dos clientes</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Gráficos */}
            <Row className="mb-4">
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Distribuição por Tipo de Cliente</h6>
                  </Card.Header>
                  <Card.Body>
                    {getCustomerTypeChartData() ? (
                      <Chart
                        options={getCustomerTypeChartData().options}
                        series={getCustomerTypeChartData().series}
                        type="donut"
                        height={300}
                      />
                    ) : (
                      <div className="text-center p-4">
                        <p className="text-muted">Sem dados para exibir</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Canal de Aquisição</h6>
                  </Card.Header>
                  <Card.Body>
                    {getAcquisitionChannelChartData() ? (
                      <Chart
                        options={getAcquisitionChannelChartData().options}
                        series={getAcquisitionChannelChartData().series}
                        type="bar"
                        height={300}
                      />
                    ) : (
                      <div className="text-center p-4">
                        <p className="text-muted">Sem dados para exibir</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Top Tags */}
            <Row>
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0 d-flex align-items-center">
                      <IconTag className="me-2" />
                      Tags Mais Utilizadas
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <TopTagsList tags={stats.top_tags} />
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Resumo Geral</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex justify-content-between">
                        <span>Total de Clientes:</span>
                        <strong>{stats.total_customers}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Clientes Ativos:</span>
                        <strong className="text-success">
                          {stats.active_customers}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Novos Este Mês:</span>
                        <strong className="text-info">
                          {stats.new_customers_this_month}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>WhatsApp Ativo:</span>
                        <strong className="text-success">
                          {stats.whatsapp_active_percentage}%
                        </strong>
                      </div>
                      {stats.avg_age && (
                        <div className="d-flex justify-content-between">
                          <span>Idade Média:</span>
                          <strong>{stats.avg_age} anos</strong>
                        </div>
                      )}
                    </div>

                    <hr />

                    <div>
                      <h6 className="mb-2">Principais Insights:</h6>
                      <ul className="list-unstyled">
                        <li className="mb-1">
                          <small>
                            •{" "}
                            {Math.round(
                              (stats.active_customers / stats.total_customers) *
                                100
                            )}
                            % dos clientes estão ativos
                          </small>
                        </li>
                        <li className="mb-1">
                          <small>
                            • {stats.whatsapp_active_percentage}% têm WhatsApp
                            ativo
                          </small>
                        </li>
                        <li className="mb-1">
                          <small>
                            • {stats.new_customers_this_month} novos clientes
                            este mês
                          </small>
                        </li>
                        {stats.top_tags && stats.top_tags.length > 0 && (
                          <li className="mb-1">
                            <small>
                              • Tag mais usada: "{stats.top_tags[0].tag__name}"
                              ({stats.top_tags[0].count} clientes)
                            </small>
                          </li>
                        )}
                      </ul>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Alert variant="warning">
            <IconChartBar className="me-2" />
            Não foi possível carregar as estatísticas.
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CustomerStatsModal;
